// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { useMemo } from "react";

import { type CommentWithOptionalTags } from "./reviewCommentTags";

import {
  claimCitationsFor,
  placedCommentMemoryIds,
} from "../../components/ask-puntt-review/claimCitations";
import { type LinkableClaim } from "../../plugins/rehype-claim-citation";
import {
  type LinkableMemory,
  memoryLinksFor,
} from "../../utils/memoryCitationFields";

/**
 * The saved memories and the claims each comment in the list cites, keyed by
 * comment id.
 */
export function useReviewCommentCitations(
  comments: readonly CommentWithOptionalTags[],
): {
  memoryCitationsByComment: ReadonlyMap<string, LinkableMemory[]>;
  claimCitationsByComment: ReadonlyMap<string, LinkableClaim[]>;
} {
  // The saved memories each comment rests on, numbered over the comment's own
  // text, and the claims it links. Memories come first: a memory whose
  // sentence takes a chip must not also link the claim wording it hangs on.
  //
  // Derived once per comment list, not inside the render map. Called inline it
  // handed `AskPunttReviewCommentBody` a fresh array identity on every render
  // of this list -- and that array is what `Markdown` keys its rehype-plugin
  // memo on, so every unrelated re-render (a tag filter, a selection) rebuilt
  // the unified processor for every comment on screen. `ChatMessage` already
  // memoises the same derivation; this is the list doing the same.
  const { memoryCitationsByComment, claimCitationsByComment } = useMemo(() => {
    const memories = new Map<string, LinkableMemory[]>();
    const claims = new Map<string, LinkableClaim[]>();
    for (const comment of comments) {
      const linked = memoryLinksFor(
        comment.citations,
        comment.description,
        comment._id,
      );
      memories.set(comment._id, linked);
      const placed = placedCommentMemoryIds(comment.description, linked);
      claims.set(comment._id, claimCitationsFor(comment, undefined, placed));
    }
    return {
      memoryCitationsByComment: memories,
      claimCitationsByComment: claims,
    };
  }, [comments]);

  return { memoryCitationsByComment, claimCitationsByComment };
}
